import React, { useState, useRef, useEffect } from "react";
import { TODAY, fmtSAR, fmtCompact, fmtShort } from "../../utils/format";
import { pnl, pnlPct, catName, locName } from "../../utils/helpers";
import { fetchForexRate } from "../../utils/forex";
import { vehRegExpiry } from "../../permissions/checks";
import { DashDonut, DM } from "../../shared/charts/Donut";
import AIPanel from "../../shared/AIPanel";
import Modal from "../../shared/Modal";

/* ══ COMMAND CENTER (Dashboard) — iOS-style widget dashboard ═ */
const DASHBOARD_WIDGET_DEFS = [
  {key:"finances",     label:"Financial Summary",    labelAr:"الملخص المالي",            group:"Finance",    groupAr:"الماليات"},
  {key:"portfolio",    label:"Portfolio & Net Worth",labelAr:"المحفظة وصافي الثروة",      group:"Finance",    groupAr:"الماليات"},
  {key:"actions",      label:"Needs Attention",      labelAr:"يتطلّب الانتباه",           group:"Execution",  groupAr:"التنفيذ"},
  {key:"tasks",        label:"Tasks & Execution",    labelAr:"المهام والتنفيذ",           group:"Execution",  groupAr:"التنفيذ"},
  {key:"schedule",     label:"Today's Schedule",     labelAr:"جدول اليوم",                 group:"Calendar",   groupAr:"التقويم"},
  {key:"operations",   label:"Operations Overview",  labelAr:"نظرة عامة على العمليات",    group:"Operations", groupAr:"العمليات"},
  {key:"recentExp",    label:"Recent Expenses",      labelAr:"المصروفات الأخيرة",          group:"Finance",    groupAr:"الماليات"},
  {key:"projects",     label:"Active Projects",      labelAr:"المشاريع النشطة",            group:"Projects",   groupAr:"المشاريع"},
  {key:"staff",        label:"Staff & HR",           labelAr:"الموظفون والموارد البشرية",  group:"Staff",      groupAr:"الموظفون"},
  {key:"fleet",        label:"Fleet & Vehicles",     labelAr:"الأسطول والمركبات",          group:"Fleet",      groupAr:"الأسطول"},
  {key:"incomeBreak",  label:"Income Breakdown",     labelAr:"تفصيل الدخل",                group:"Finance",    groupAr:"الماليات"},
  {key:"spendCats",    label:"Spending by Category", labelAr:"الإنفاق حسب الفئة",          group:"Finance",    groupAr:"الماليات"},
  {key:"cashTrend",    label:"Cash Flow Trend",      labelAr:"اتجاه التدفق النقدي",        group:"Finance",    groupAr:"الماليات"},
  {key:"investAlloc",  label:"Investment Allocation",labelAr:"توزيع الاستثمار",            group:"Finance",    groupAr:"الماليات"},
  {key:"weekAhead",    label:"Week Ahead",           labelAr:"الأسبوع القادم",             group:"Calendar",   groupAr:"التقويم"},
  {key:"projHealth",   label:"Project Health",       labelAr:"صحة المشاريع",               group:"Projects",   groupAr:"المشاريع"},
  {key:"staffInsight", label:"Staff Insights",       labelAr:"إحصاءات الموظفين",           group:"Staff",      groupAr:"الموظفون"},
  {key:"household",    label:"Household",            labelAr:"المنزل",                      group:"Operations", groupAr:"العمليات"},
  {key:"facilMaint",   label:"Facility Maintenance", labelAr:"صيانة المنشأة",              group:"Operations", groupAr:"العمليات"},
  {key:"transport",    label:"Transport",            labelAr:"النقل",                       group:"Operations", groupAr:"العمليات"},
  {key:"menuPlan",     label:"Menu Planning",        labelAr:"تخطيط القائمة",              group:"Operations", groupAr:"العمليات"},
  {key:"suppliers",    label:"Suppliers",            labelAr:"الموردون",                    group:"Operations", groupAr:"العمليات"},
  {key:"fleetValue",   label:"Fleet Overview",       labelAr:"نظرة على الأسطول",           group:"Fleet",      groupAr:"الأسطول"},
  {key:"smartHome",    label:"Smart Home",           labelAr:"المنزل الذكي",               group:"Operations", groupAr:"العمليات"},
];
const DEFAULT_DASHBOARD_ORDER = DASHBOARD_WIDGET_DEFS.map(w=>w.key);

/* ── Metric row — label left, value right ── */
function CommandCenter({state,dispatch,t,setView}){
  const {income,expenses,holdings,meetings,calAccounts,locFilter,locations,lang,projects,groceries,vehicles,staff,leaveRequests,loanRequests,maintenance,transportRequests}=state;
  const isRTL=lang==="ar";
  const currentUser=state.currentUser||{};
  /* ── Theme accent shades — dark = positive/income, light = negative/expense ── */
  const _accent=state.branding?.anchorColor||"#C9A84C";
  const acDark=`color-mix(in srgb, ${_accent} 75%, #000)`;
  const acMid=_accent;
  const acLight=`color-mix(in srgb, ${_accent} 55%, #888)`;
  const acFade=`color-mix(in srgb, ${_accent} 25%, #ccc)`;
  const acDarkBg=`color-mix(in srgb, ${_accent} 12%, #fff)`;
  const acLightBg=`color-mix(in srgb, ${_accent} 6%, #f5f5f5)`;
  const acBorder=`color-mix(in srgb, ${_accent} 25%, #e0e0e0)`;
  const now2=new Date();
  const TODAY=now2.toISOString().slice(0,10);
  const monthName=now2.toLocaleDateString(isRTL?"ar-SA":"en-GB",{month:"long",year:"numeric"});
  const greeting=(()=>{const h=now2.getHours();return h<12?(isRTL?"صباح الخير":"Good morning"):h<17?(isRTL?"مساء الخير":"Good afternoon"):(isRTL?"مساء الخير":"Good evening");})();

  /* Forex conversion for multi-currency holdings + investment liabilities */
  const wsCur=state.currency||"SAR";
  const [fxRates,setFxRates]=useState({});
  const _invLiabsAll=state.investmentLiabilities||[];
  useEffect(()=>{
    const foreignCurs=[...new Set([...(holdings||[]).map(h=>h.currency),..._invLiabsAll.map(l=>l.currency)].filter(c=>c&&c!==wsCur))];
    if(!foreignCurs.length)return;
    Promise.all(foreignCurs.map(c=>fetchForexRate(c,wsCur).then(rate=>({c,rate})))).then(results=>{
      const rates={};
      results.forEach(({c,rate})=>{if(rate)rates[c]=rate;});
      setFxRates(rates);
    });
  },[(holdings||[]).length,_invLiabsAll.length,wsCur]);
  const toWsCur=(amount,cur)=>{
    if(!cur||cur===wsCur)return amount||0;
    return (amount||0)*(fxRates[cur]||1);
  };

  /* ── Tasks ── */
  const allTasks=(state.tasks||[]).filter(tk=>!tk.deletedAt);
  const fl=locFilter?allTasks.filter(tk=>!tk.locId||tk.locId===locFilter):allTasks;
  const open=fl.filter(tk=>tk.status!=="completed"&&tk.status!=="cancelled");
  const high=open.filter(tk=>tk.priority==="high"||tk.priority==="urgent");
  const overdue=open.filter(tk=>tk.dueDate&&new Date(tk.dueDate)<now2);
  const waiting=fl.filter(tk=>tk.approvalStatus==="pending");
  const dueToday=open.filter(tk=>tk.dueDate&&tk.dueDate===TODAY);
  const completedThisWeek=allTasks.filter(tk=>{if(tk.status!=="completed")return false;const d=new Date(tk.updatedAt||tk.createdAt);return(now2-d)<7*86400000;});

  /* ── Finance — MTD ── */
  const ms=now2.toISOString().slice(0,7)+"-01";
  const monthInc=income.filter(i=>i.date>=ms&&i.date<=TODAY&&i.status==="Realised"&&(!i.finType||i.finType==="Income")).reduce((a,i)=>a+i.amount,0);
  const monthExp=expenses.filter(e=>e.date>=ms&&e.date<=TODAY&&(!e.finType||e.finType==="Expense")).reduce((a,e)=>a+e.amount,0);
  const netCF=monthInc-monthExp;
  const unrealised=income.filter(i=>i.status==="Unrealised").reduce((a,i)=>a+i.amount,0);
  const pendingApprovals=expenses.filter(e=>!e.approved&&!e.rejected).length;
  const recurringExpAmt=(state.recurringExpenses||[]).filter(r=>r.active&&r.frequency==="Monthly").reduce((a,r)=>a+r.amount,0);

  /* ── Balance Sheet & Net Worth ── */
  const bsItems=state.balanceItems||[];
  const invLiabs=_invLiabsAll.filter(l=>l.status==="active");
  const totalAssets=bsItems.filter(b=>b.finType==="Asset").reduce((a,b)=>a+b.amount,0)+holdings.reduce((a,h)=>a+toWsCur(h.currentVal,h.currency),0)+(vehicles||[]).reduce((a,v)=>a+(v.currentVal||0),0);
  const totalLiabilities=bsItems.filter(b=>b.finType==="Liability").reduce((a,b)=>a+b.amount,0)+invLiabs.reduce((a,l)=>a+toWsCur(l.outstandingBalance||0,l.currency),0);
  const netWorth=totalAssets-totalLiabilities;

  /* ── Portfolio ── */
  const totalP=holdings.reduce((a,h)=>a+toWsCur(h.currentVal,h.currency),0);
  const totalI=holdings.reduce((a,h)=>a+toWsCur(h.invested,h.currency),0);
  const totalPnL=totalP-totalI;
  const pnlPctTotal=totalI>0?((totalPnL/totalI)*100).toFixed(1):"0";
  const monthlyDebt=invLiabs.reduce((a,l)=>a+toWsCur(l.monthlyPayment||0,l.currency),0);
  const upcomingPayments=invLiabs.filter(l=>l.nextPaymentDate&&l.nextPaymentDate>=TODAY&&l.nextPaymentDate<=new Date(now2.getTime()+7*86400000).toISOString().slice(0,10));

  /* ── Operations ── */
  const activeProjects=(projects||[]).filter(p=>p.status==="active");
  const overBudgetProjects=activeProjects.filter(p=>p.budget&&(expenses.filter(e=>e.projectId===p.id).reduce((a,e)=>a+e.amount,0))>p.budget);
  const pendingLeave=(leaveRequests||[]).filter(r=>r.status==="pending");
  const pendingLoans=(loanRequests||[]).filter(r=>r.status==="pending");
  const activeStaff=(staff||[]).filter(s=>s.status!=="inactive");
  const staffDocExpiring=(staff||[]).reduce((arr,s)=>{(s.docs||[]).forEach(d=>{if(d.expiryDate){const days=Math.ceil((new Date(d.expiryDate)-now2)/86400000);if(days>0&&days<=30)arr.push({staff:s.name,doc:d.name,days});}});return arr;},[]);
  const fleetSvcWarnKm=parseInt((state.fleetConfig||{}).serviceWarningKm)||0;
  const fleetAlerts=(vehicles||[]).filter(v=>(v.status||"Active")==="Active"&&((v.nextServiceOdom&&(v.odom+fleetSvcWarnKm)>=v.nextServiceOdom)||new Date(v.nextServiceDate||"2099")<now2||new Date(v.insuranceExpiry||"2099")<now2||new Date(vehRegExpiry(v)||"2099")<now2));
  const pendingGroceries=(groceries||[]).filter(g=>g.status==="pending"||g.status==="Pending");
  const openMaint=(maintenance||[]).filter(m=>m.status==="Open"||m.status==="pending"||m.status==="scheduled");
  const unassignedMaint=openMaint.filter(m=>!m.assignedTo);
  const todayTransport=(transportRequests||[]).filter(r=>r.date===TODAY&&r.status==="scheduled");
  const todayMeetings=(meetings||[]).filter(m=>m.date===TODAY&&(calAccounts||[]).find(c=>c.id===m.accountId)?.active).sort((a,b)=>(a.time||"").localeCompare(b.time||""));
  const contractWarningDays=(state.supplierConfig||{}).contractExpiryWarningDays||30;
  const expiringContracts=(state.suppliers||[]).filter(s=>{if(!s.contractExpiry)return false;const d=Math.ceil((new Date(s.contractExpiry)-now2)/86400000);return d>0&&d<=contractWarningDays;});
  const recentExp=[...expenses].sort((a,b)=>b.date.localeCompare(a.date)).slice(0,6);
  const locLabel=locFilter?locName(locations,locFilter):"";

  /* ── Income breakdown ── */
  const recurringInc=(state.recurringIncome||[]).filter(r=>r.active);
  const monthlyRecurInc=recurringInc.filter(r=>r.frequency==="Monthly").reduce((a,r)=>a+r.amount,0);
  const incByCat=income.filter(i=>i.date>=ms&&i.date<=TODAY&&i.status==="Realised").reduce((m,i)=>{const cn=catName(state.incomeCats||[],i.catId);m[cn]=(m[cn]||0)+i.amount;return m;},{});
  const incCatArr=Object.entries(incByCat).sort((a,b)=>b[1]-a[1]);
  const overdueIncome=income.filter(i=>i.status==="Unrealised"&&i.date<TODAY);

  /* ── Expense by category ── */
  const expByCat=expenses.filter(e=>e.date>=ms&&e.date<=TODAY&&(!e.finType||e.finType==="Expense")).reduce((m,e)=>{const cn=catName(state.expenseCats||[],e.catId);m[cn]=(m[cn]||0)+e.amount;return m;},{});
  const expCatArr=Object.entries(expByCat).sort((a,b)=>b[1]-a[1]);
  const recurringExpenses=state.recurringExpenses||[];
  const recurringRenewals=recurringExpenses.filter(r=>r.active&&r.nextDate&&r.nextDate>=TODAY&&r.nextDate<=new Date(now2.getTime()+7*86400000).toISOString().slice(0,10));

  /* ── Cash flow trend (last 4 months) ── */
  const cfMonths=[];
  for(let mi=3;mi>=0;mi--){
    const d=new Date(now2.getFullYear(),now2.getMonth()-mi,1);
    const mStart=d.toISOString().slice(0,10);
    const mEnd=new Date(d.getFullYear(),d.getMonth()+1,0).toISOString().slice(0,10);
    const mLabel=d.toLocaleDateString(isRTL?"ar-SA":"en-GB",{month:"short"});
    const mInc=income.filter(i=>i.date>=mStart&&i.date<=mEnd&&i.status==="Realised"&&(!i.finType||i.finType==="Income")).reduce((a,i)=>a+i.amount,0);
    const mExp=expenses.filter(e=>e.date>=mStart&&e.date<=mEnd&&(!e.finType||e.finType==="Expense")).reduce((a,e)=>a+e.amount,0);
    cfMonths.push({label:mLabel,inc:mInc,exp:mExp,net:mInc-mExp});
  }
  const cfMax=Math.max(...cfMonths.map(m=>Math.max(m.inc,m.exp)),1);

  /* ── Investment allocation ── */
  const holdingsByCat=holdings.reduce((m,h)=>{const cat=h.type||h.category||(isRTL?"أخرى":"Other");m[cat]=(m[cat]||0)+toWsCur(h.currentVal,h.currency);return m;},{});
  const holdingCatArr=Object.entries(holdingsByCat).sort((a,b)=>b[1]-a[1]);
  const liabMaturing=invLiabs.filter(l=>l.maturityDate&&l.maturityDate>=TODAY&&l.maturityDate<=new Date(now2.getTime()+90*86400000).toISOString().slice(0,10));

  /* ── Week ahead (calendar) ── */
  const weekEnd=new Date(now2.getTime()+7*86400000).toISOString().slice(0,10);
  const weekMeetings=(meetings||[]).filter(m=>m.date>TODAY&&m.date<=weekEnd&&(calAccounts||[]).find(c=>c.id===m.accountId)?.active).sort((a,b)=>a.date.localeCompare(b.date)||((a.time||"").localeCompare(b.time||"")));
  const weekTransport=(transportRequests||[]).filter(r=>r.date>TODAY&&r.date<=weekEnd&&(r.status==="scheduled"||r.status==="pending"));

  /* ── Project health ── */
  const behindProjects=activeProjects.filter(p=>p.endDate&&p.endDate<TODAY&&p.status==="active");
  const projMilestones=(state.projectWorkstreams||[]).filter(w=>w.status!=="completed"&&w.dueDate&&w.dueDate<=weekEnd);

  /* ── Staff insights ── */
  const totalPayroll=activeStaff.reduce((a,s)=>a+(s.salary||0),0);
  const staffByRole=activeStaff.reduce((m,s)=>{const r=s.role||"Staff";m[r]=(m[r]||0)+1;return m;},{});
  const staffRoleArr=Object.entries(staffByRole).sort((a,b)=>b[1]-a[1]);
  const activeLoans=(staff||[]).reduce((a,s)=>(s.loans||[]).filter(l=>l.status==="active").length+a,0);

  /* ── Household ── */
  const totalGroceries=(groceries||[]).length;
  const fulfilledGroceries=(groceries||[]).filter(g=>g.status==="fulfilled"||g.status==="Fulfilled"||g.status==="completed").length;
  const groceryPct=totalGroceries>0?Math.round(fulfilledGroceries/totalGroceries*100):0;
  /* ── Facility maintenance ── */
  const maintCosts=(maintenance||[]).filter(m=>m.cost&&m.date>=ms&&m.date<=TODAY).reduce((a,m)=>a+(m.cost||0),0);
  const overdueMaint=(maintenance||[]).filter(m=>(m.status==="Open"||m.status==="pending"||m.status==="scheduled")&&m.dueDate&&m.dueDate<TODAY);
  const scheduledMaint=(maintenance||[]).filter(m=>m.status==="scheduled"&&m.dueDate&&m.dueDate>=TODAY&&m.dueDate<=weekEnd);

  /* ── Transport ── */
  const upcomingTransport=(transportRequests||[]).filter(r=>r.date>=TODAY&&r.date<=weekEnd&&(r.status==="scheduled"||r.status==="pending")).sort((a,b)=>a.date.localeCompare(b.date));

  /* ── Menu planning ── */
  const activeCycle=(state.menuCycles||[]).find(c=>c.startDate<=TODAY&&c.endDate>=TODAY);
  const todayMeals=activeCycle?(state.menuGeneratedOptions||[]).filter(o=>o.cycleId===activeCycle.id&&o.day===TODAY&&!o.deletedAt):[];
  const totalMeals=(state.meals||[]).length;

  /* ── Suppliers ── */
  const allSuppliers=state.suppliers||[];
  const activeSuppliers=allSuppliers.filter(s=>s.status!=="inactive");
  const avgRating=activeSuppliers.length>0?(activeSuppliers.reduce((a,s)=>a+(s.rating||0),0)/activeSuppliers.filter(s=>s.rating).length).toFixed(1):"—";

  /* ── Fleet overview ── */
  const totalFleetValue=(vehicles||[]).reduce((a,v)=>a+(v.currentVal||0),0);
  const totalFleetPurchase=(vehicles||[]).reduce((a,v)=>a+(v.purchasePrice||0),0);
  const fleetDepreciation=totalFleetPurchase-totalFleetValue;
  const avgMileage=(vehicles||[]).length>0?Math.round((vehicles||[]).reduce((a,v)=>a+(v.odom||0),0)/(vehicles||[]).length):0;

  /* ── Action items — everything needing attention ── */
  const actions=[];
  if(overdue.length) actions.push({l:isRTL?`${overdue.length} مهام متأخرة`:`${overdue.length} overdue task${overdue.length>1?"s":""}`,n:overdue.length,icon:"!",nav:"Execution"});
  if(pendingApprovals) actions.push({l:isRTL?`${pendingApprovals} مصروفات بانتظار الموافقة`:`${pendingApprovals} expense${pendingApprovals>1?"s":""} awaiting approval`,n:pendingApprovals,icon:"$",nav:"Expenses"});
  if(pendingLeave.length) actions.push({l:isRTL?`${pendingLeave.length} طلبات إجازة`:`${pendingLeave.length} leave request${pendingLeave.length>1?"s":""}`,n:pendingLeave.length,icon:"→",nav:"Staff"});
  if(pendingLoans.length) actions.push({l:isRTL?`${pendingLoans.length} طلبات سلف`:`${pendingLoans.length} loan request${pendingLoans.length>1?"s":""}`,n:pendingLoans.length,icon:"$",nav:"Staff"});
  if(fleetAlerts.length) actions.push({l:isRTL?`${fleetAlerts.length} تنبيهات أسطول`:`${fleetAlerts.length} fleet alert${fleetAlerts.length>1?"s":""}`,n:fleetAlerts.length,icon:"⚠",nav:"Fleet"});
  if(openMaint.length) actions.push({l:isRTL?`${openMaint.length} مشاكل صيانة (${unassignedMaint.length} غير مسنّدة)`:`${openMaint.length} maintenance issue${openMaint.length>1?"s":""} (${unassignedMaint.length} unassigned)`,n:openMaint.length,icon:"🔧",nav:"Facility"});
  if(staffDocExpiring.length) actions.push({l:isRTL?`${staffDocExpiring.length} وثائق موظفين تنتهي قريباً`:`${staffDocExpiring.length} staff doc${staffDocExpiring.length>1?"s":""} expiring within 30 days`,n:staffDocExpiring.length,icon:"📄",nav:"Staff"});
  if(expiringContracts.length) actions.push({l:isRTL?`${expiringContracts.length} عقود موردين تنتهي`:`${expiringContracts.length} supplier contract${expiringContracts.length>1?"s":""} expiring soon`,n:expiringContracts.length,icon:"📋",nav:"Suppliers"});
  if(upcomingPayments.length) actions.push({l:isRTL?`${upcomingPayments.length} دفعات فوائد هذا الأسبوع`:`${upcomingPayments.length} interest payment${upcomingPayments.length>1?"s":""} due this week`,n:upcomingPayments.length,icon:"💳",nav:"Investments"});
  if(overBudgetProjects.length) actions.push({l:isRTL?`${overBudgetProjects.length} مشاريع تجاوزت الميزانية`:`${overBudgetProjects.length} project${overBudgetProjects.length>1?"s":""} over budget`,n:overBudgetProjects.length,icon:"📊",nav:"Projects"});

  /* ── Dashboard config ── */
  const cfg=state.dashboardConfig||{};
  const savedOrder=cfg.order||DEFAULT_DASHBOARD_ORDER;
  /* Append any new widgets not yet in the user's saved order */
  const allKeys=DASHBOARD_WIDGET_DEFS.map(w=>w.key);
  const newKeys=allKeys.filter(k=>!savedOrder.includes(k));
  const widgetOrder=newKeys.length>0?[...savedOrder,...newKeys]:savedOrder;
  const widgetHidden=cfg.hidden||[];
  const [showCustomize,setShowCustomize]=useState(false);
  const [draftOrder,setDraftOrder]=useState(widgetOrder);
  const [draftHidden,setDraftHidden]=useState(widgetHidden);
  const [dragIdx,setDragIdx]=useState(null);
  const [dragOverIdx,setDragOverIdx]=useState(null);
  const saveCustomize=()=>{dispatch({type:"SET_DASHBOARD_CONFIG",config:{order:draftOrder,hidden:draftHidden}});setShowCustomize(false);};
  const handleDragStart=(e,i)=>{setDragIdx(i);e.dataTransfer.effectAllowed="move";};
  const handleDragOver=(e,i)=>{e.preventDefault();if(dragOverIdx!==i){setDragOverIdx(i);if(dragIdx!==null&&dragIdx!==i){const n=[...draftOrder];const [item]=n.splice(dragIdx,1);n.splice(i,0,item);setDraftOrder(n);setDragIdx(i);}}};
  const handleDrop=()=>{setDragIdx(null);setDragOverIdx(null);};
  const handleDragEnd=()=>{setDragIdx(null);setDragOverIdx(null);};
  /* Touch drag — live reorder with visual lift */
  const touchRef=useRef(null);
  const [touchDragIdx,setTouchDragIdx]=useState(null);
  const [touchDragY,setTouchDragY]=useState(0);
  const handleTouchStart=(e,i)=>{const rect=e.currentTarget.getBoundingClientRect();touchRef.current={idx:i,startY:e.touches[0].clientY,offsetY:e.touches[0].clientY-rect.top,itemH:rect.height,moved:false,origOrder:[...draftOrder]};};
  const handleTouchMove=(e)=>{if(!touchRef.current)return;const touchY=e.touches[0].clientY;const dy=touchY-touchRef.current.startY;if(!touchRef.current.moved&&Math.abs(dy)<8)return;if(!touchRef.current.moved){touchRef.current.moved=true;setTouchDragIdx(touchRef.current.idx);}e.preventDefault();setTouchDragY(dy);/* Live reorder */const container=e.currentTarget.closest('.mb');if(!container)return;const items=container.querySelectorAll('[data-drag-item]');let targetIdx=touchRef.current.idx;for(let j=0;j<items.length;j++){const r=items[j].getBoundingClientRect();if(touchY>=r.top&&touchY<=r.bottom){targetIdx=j;break;}}if(targetIdx!==touchRef.current.idx){const n=[...draftOrder];const [item]=n.splice(touchRef.current.idx,1);n.splice(targetIdx,0,item);setDraftOrder(n);touchRef.current.startY=touchY;setTouchDragY(0);touchRef.current.idx=targetIdx;setTouchDragIdx(targetIdx);}};
  const handleTouchEnd=()=>{if(!touchRef.current)return;touchRef.current=null;setTouchDragIdx(null);setTouchDragY(0);};
  const openCustomize=()=>{setDraftOrder(widgetOrder);setDraftHidden(widgetHidden);setShowCustomize(true);};
  const isVisible=(key)=>!widgetHidden.includes(key);
  const orderedWidgets=widgetOrder.filter(k=>DASHBOARD_WIDGET_DEFS.find(d=>d.key===k));

  const aiCtx=`Monthly: Income ${fmtSAR(monthInc)}, Expenses ${fmtSAR(monthExp)}, Net ${fmtSAR(netCF)}. Unrealised ${fmtSAR(unrealised)}. Recurring income ${fmtSAR(monthlyRecurInc)}/mo. Top expense cats: ${expCatArr.slice(0,3).map(([c,a])=>`${c} ${fmtShort(a)}`).join(", ")||"none"}. Assets ${fmtSAR(totalAssets)}, Liabilities ${fmtSAR(totalLiabilities)}, Net Worth ${fmtSAR(netWorth)}. Portfolio ${fmtSAR(totalP)} (${totalPnL>=0?"+":""}${fmtSAR(totalPnL)}). ${holdingCatArr.map(([c,v])=>`${c}: ${fmtShort(v)}`).join(", ")||"no holdings"}. ${open.length} open tasks, ${overdue.length} overdue, ${high.length} high priority. ${activeProjects.length} active projects${overBudgetProjects.length?`, ${overBudgetProjects.length} over budget`:""}${behindProjects.length?`, ${behindProjects.length} behind schedule`:""}. ${pendingApprovals} expense approvals pending. ${fleetAlerts.length} fleet alerts, fleet value ${fmtSAR(totalFleetValue)}. ${openMaint.length} open maintenance (${unassignedMaint.length} unassigned), ${overdueMaint.length} overdue. ${activeStaff.length} active staff, payroll ${fmtSAR(totalPayroll)}/mo, ${pendingLeave.length} leave + ${pendingLoans.length} loan requests. ${pendingGroceries.length} pending groceries. ${todayTransport.length} rides today, ${upcomingTransport.length} this week. ${todayMeetings.length} meetings today, ${weekMeetings.length} this week. ${activeSuppliers.length} suppliers, ${expiringContracts.length} contracts expiring.`;

  /* ── Widget renderer ── */
  const W=(key)=>{
    if(!isVisible(key)) return null;
    switch(key){

      /* ════ FINANCIAL SUMMARY ════ */
      case "finances": return(
        <div key={key} className="dash-w">
          <div className="dash-w-hd"><div className="dash-w-title">{isRTL?"الملخص المالي":"Financial Summary"}</div><div className="dash-w-sub">{monthName}</div></div>
          <div className="dash-w-body">
            {/* Income vs Expense bar */}
            <div style={{display:"flex",height:20,borderRadius:10,overflow:"hidden",marginBottom:14}}>
              {(monthInc+monthExp)>0?<>
                <div style={{width:`${Math.max(monthInc/(monthInc+monthExp)*100,4)}%`,background:acDark,transition:"width .4s"}}/>
                <div style={{flex:1,background:acFade,transition:"width .4s"}}/>
              </>:<div style={{flex:1,background:"var(--g200)"}}/>}
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"0 16px"}}>
              <DM l={isRTL?"الدخل المحقق":"Realised Income"} v={fmtCompact(monthInc)} c={acDark} bold/>
              <DM l={isRTL?"المصروفات":"Expenses"} v={fmtCompact(monthExp)} c={acLight} bold/>
              <DM l={isRTL?"غير محقق":"Unrealised"} v={fmtCompact(unrealised)} c={acMid}/>
              <DM l={netCF>=0?(isRTL?"فائض":"Surplus"):(isRTL?"عجز":"Deficit")} v={`${netCF>=0?"+":""}${fmtCompact(netCF)}`} c={netCF>=0?acDark:acLight} bold/>
              {recurringExpAmt>0&&<DM l={isRTL?"التزامات شهرية":"Monthly Commitments"} v={fmtCompact(recurringExpAmt)} c="var(--g600)"/>}
              {pendingApprovals>0&&<DM l={isRTL?"بانتظار الموافقة":"Pending Approval"} v={pendingApprovals+" "+(isRTL?"مصروف":"exp")} c={acMid}/>}
            </div>
          </div>
        </div>
      );

      /* ════ PORTFOLIO & NET WORTH ════ */
      case "portfolio": return(
        <div key={key} className="dash-w">
          <div className="dash-w-hd"><div className="dash-w-title">{isRTL?"المحفظة والثروة":"Portfolio & Net Worth"}</div></div>
          <div className="dash-w-body">
            {/* Net Worth headline */}
            <div style={{display:"flex",alignItems:"baseline",gap:8,marginBottom:10}}>
              <span style={{fontFamily:"var(--fm)",fontSize:24,fontWeight:700,color:"var(--ink)"}}>{fmtCompact(netWorth)}</span>
              <span style={{fontSize:11,color:"var(--g400)"}}>{isRTL?"صافي الثروة":"net worth"}</span>
            </div>
            {/* Assets vs Liabilities mini bar */}
            <div style={{display:"flex",height:6,borderRadius:3,overflow:"hidden",marginBottom:12}}>
              <div style={{width:`${totalAssets+totalLiabilities>0?Math.max(totalAssets/(totalAssets+totalLiabilities)*100,5):80}%`,background:acDark,borderRadius:3}}/>
              {totalLiabilities>0&&<div style={{flex:1,background:acFade,borderRadius:3,marginInlineStart:2}}/>}
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"0 16px",marginBottom:12,paddingBottom:12,borderBottom:`1px solid ${acBorder}`}}>
              <DM l={isRTL?"الأصول":"Assets"} v={fmtCompact(totalAssets)} c={acDark}/>
              <DM l={isRTL?"الالتزامات":"Liabilities"} v={fmtCompact(totalLiabilities)} c={totalLiabilities>0?acLight:"var(--g400)"}/>
            </div>
            {/* Portfolio section */}
            <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:8}}>
              <DashDonut value={Math.abs(totalPnL)} max={totalI||1} color={totalPnL>=0?acDark:acLight} label={`${totalPnL>=0?"+":""}${pnlPctTotal}%`}/>
              <div style={{flex:1}}>
                <DM l={isRTL?"قيمة المحفظة":"Portfolio Value"} v={fmtCompact(totalP)} bold/>
                <DM l={isRTL?"المستثمر":"Invested"} v={fmtCompact(totalI)}/>
                <DM l={isRTL?"الربح/الخسارة":"P&L"} v={`${totalPnL>=0?"+":""}${fmtCompact(totalPnL)}`} c={totalPnL>=0?acDark:acLight} bold/>
              </div>
            </div>
            {/* Top holdings */}
            {holdings.length>0&&<div style={{display:"flex",flexWrap:"wrap",gap:4,marginTop:8}}>
              {[...holdings].sort((a,b)=>toWsCur(b.currentVal,b.currency)-toWsCur(a.currentVal,a.currency)).slice(0,5).map(h=><span key={h.id} style={{fontSize:10,padding:"2px 8px",borderRadius:10,background:acLightBg,color:"var(--g600)",fontFamily:"var(--fm)",border:`1px solid ${acBorder}`}}>{h.ticker||h.name?.slice(0,10)} <span style={{color:pnl(h)>=0?acDark:acLight,fontWeight:600}}>{pnl(h)>=0?"+":""}{pnlPct(h)}%</span></span>)}
            </div>}
            {/* Debt service */}
            {monthlyDebt>0&&<div style={{marginTop:10,paddingTop:10,borderTop:`1px solid ${acBorder}`}}><DM l={isRTL?"خدمة الدين الشهرية":"Monthly Debt Service"} v={fmtCompact(monthlyDebt)} c={acLight}/></div>}
          </div>
        </div>
      );

      /* ════ NEEDS ATTENTION ════ */
      case "actions": return actions.length>0?(
        <div key={key} className="dash-w">
          <div className="dash-w-hd"><div className="dash-w-title">{isRTL?"يحتاج انتباهك":"Needs Attention"}</div><div className="dash-w-sub">{actions.reduce((a,i)=>a+i.n,0)} {isRTL?"عنصر":"item"}{actions.reduce((a,i)=>a+i.n,0)!==1&&!isRTL?"s":""}</div></div>
          <div className="dash-w-body" style={{padding:"6px 0"}}>
            {actions.map((a,i)=>(
              <div key={i} onClick={()=>a.nav&&setView&&setView(a.nav)} style={{display:"flex",alignItems:"center",gap:12,padding:"11px 18px",cursor:a.nav?"pointer":"default",transition:"background .12s",borderBottom:i<actions.length-1?"1px solid var(--g50)":"none"}} onMouseDown={e=>{if(a.nav)e.currentTarget.style.background="var(--g50)"}} onMouseUp={e=>{e.currentTarget.style.background="transparent"}} onMouseLeave={e=>{e.currentTarget.style.background="transparent"}}>
                <span style={{width:32,height:32,borderRadius:10,background:acDarkBg,color:acDark,display:"flex",alignItems:"center",justifyContent:"center",fontSize:13,flexShrink:0}}>{a.icon}</span>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontSize:13,fontWeight:500,color:"var(--ink)",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{a.l}</div>
                </div>
                {a.nav&&<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--g300)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{flexShrink:0}}><polyline points={isRTL?"15 18 9 12 15 6":"9 6 15 12 9 18"}/></svg>}
              </div>
            ))}
          </div>
        </div>
      ):null;

      /* ════ TASKS & EXECUTION ════ */
      case "tasks": return(
        <div key={key} className="dash-w">
          <div className="dash-w-hd"><div className="dash-w-title">{isRTL?"المهام":"Tasks"}</div></div>
          <div className="dash-w-body">
            <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:8,marginBottom:10}}>
              <div className="dash-qs"><div className="dash-qs-val">{open.length}</div><div className="dash-qs-lbl">{isRTL?"مفتوح":"Open"}</div></div>
              <div className="dash-qs" style={{background:overdue.length?acDarkBg:"var(--g50)"}}><div className="dash-qs-val" style={{color:overdue.length?acDark:"var(--ink)"}}>{overdue.length}</div><div className="dash-qs-lbl">{isRTL?"متأخر":"Overdue"}</div></div>
              <div className="dash-qs" style={{background:high.length?acDarkBg:"var(--g50)"}}><div className="dash-qs-val" style={{color:high.length?acMid:"var(--ink)"}}>{high.length}</div><div className="dash-qs-lbl">{isRTL?"عاجل":"Urgent"}</div></div>
              <div className="dash-qs"><div className="dash-qs-val" style={{color:acDark}}>{completedThisWeek.length}</div><div className="dash-qs-lbl">{isRTL?"أنجز":"Done"}</div></div>
            </div>
            {dueToday.length>0&&<div style={{fontSize:11,color:acMid,fontWeight:600,marginBottom:4}}>{dueToday.length} {isRTL?"مستحقة اليوم":"due today"}</div>}
            {waiting.length>0&&<div style={{fontSize:11,color:acLight,fontWeight:600}}>{waiting.length} {isRTL?"بانتظار الموافقة":"awaiting approval"}</div>}
          </div>
        </div>
      );

      /* ════ TODAY'S SCHEDULE ════ */
      case "schedule": {
        const hasItems=todayMeetings.length>0||todayTransport.length>0;
        return hasItems?(
          <div key={key} className="dash-w">
            <div className="dash-w-hd"><div className="dash-w-title">{isRTL?"جدول اليوم":"Today"}</div><div className="dash-w-sub">{now2.toLocaleDateString(isRTL?"ar-SA":"en-GB",{weekday:"long",day:"numeric",month:"short"})}</div></div>
            <div className="dash-w-body">
              {todayMeetings.map(m=>(
                <div key={m.id} style={{display:"flex",gap:10,padding:"6px 0",borderBottom:"1px solid var(--g50)"}}>
                  <div style={{fontFamily:"var(--fm)",fontSize:11,fontWeight:600,color:"var(--ink)",minWidth:40}}>{m.time?.slice(0,5)}</div>
                  <div style={{flex:1,minWidth:0}}><div style={{fontSize:12,fontWeight:500,color:"var(--ink)",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{m.title}</div>{m.location&&<div style={{fontSize:10,color:"var(--g400)"}}>{m.location}</div>}</div>
                </div>
              ))}
              {todayTransport.length>0&&<div style={{padding:"8px 0 0",fontSize:12,color:acDark,fontWeight:600}}>🚗 {todayTransport.length} {isRTL?"رحلات مجدولة":"ride"}{todayTransport.length>1&&!isRTL?"s":""} {isRTL?"":"scheduled"}</div>}
            </div>
          </div>
        ):null;
      }

      /* ════ OPERATIONS OVERVIEW ════ */
      case "operations": return(
        <div key={key} className="dash-w">
          <div className="dash-w-hd"><div className="dash-w-title">{isRTL?"العمليات":"Operations"}</div></div>
          <div className="dash-w-body">
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
              <div className="dash-qs"><div className="dash-qs-val">{pendingGroceries.length}</div><div className="dash-qs-lbl">{isRTL?"بقالة":"Groceries"}</div></div>
              <div className="dash-qs" style={{background:openMaint.length?acDarkBg:"var(--g50)"}}><div className="dash-qs-val" style={{color:openMaint.length?acMid:"var(--ink)"}}>{openMaint.length}</div><div className="dash-qs-lbl">{isRTL?"صيانة":"Maintenance"}</div></div>
              <div className="dash-qs"><div className="dash-qs-val">{todayTransport.length}</div><div className="dash-qs-lbl">{isRTL?"رحلات اليوم":"Rides Today"}</div></div>
              <div className="dash-qs" style={{background:expiringContracts.length?acDarkBg:"var(--g50)"}}><div className="dash-qs-val" style={{color:expiringContracts.length?acMid:"var(--ink)"}}>{expiringContracts.length}</div><div className="dash-qs-lbl">{isRTL?"عقود تنتهي":"Contracts"}</div></div>
            </div>
          </div>
        </div>
      );

      /* ════ RECENT EXPENSES ════ */
      case "recentExp": return recentExp.length>0?(
        <div key={key} className="dash-w">
          <div className="dash-w-hd"><div className="dash-w-title">{isRTL?"آخر المصروفات":"Recent Expenses"}</div></div>
          <div className="dash-w-body">
            {recentExp.map(e=>{
              const cn=catName(state.expenseCats||[],e.catId);
              return(
                <div key={e.id} style={{display:"flex",alignItems:"center",gap:10,padding:"6px 0",borderBottom:"1px solid var(--g50)"}}>
                  <div style={{width:28,height:28,borderRadius:8,background:e.approved?acDarkBg:e.rejected?acLightBg:acDarkBg,color:e.approved?acDark:e.rejected?acLight:acMid,display:"flex",alignItems:"center",justifyContent:"center",fontSize:10,fontWeight:700,flexShrink:0}}>{(e.expName||cn||"E").charAt(0)}</div>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontSize:12,fontWeight:500,color:"var(--ink)",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{e.expName||e.notes||cn}</div>
                    <div style={{fontSize:10,color:"var(--g400)"}}>{cn} · {e.date}{!e.approved&&!e.rejected?" · ⏳":""}</div>
                  </div>
                  <div style={{fontFamily:"var(--fm)",fontSize:12,fontWeight:600,color:acLight,flexShrink:0}}>-{fmtShort(e.amount)}</div>
                </div>
              );
            })}
          </div>
        </div>
      ):null;

      /* ════ ACTIVE PROJECTS ════ */
      case "projects": return activeProjects.length>0?(
        <div key={key} className="dash-w">
          <div className="dash-w-hd"><div className="dash-w-title">{isRTL?"المشاريع":"Projects"}</div><div className="dash-w-sub">{activeProjects.length} {isRTL?"نشط":"active"}</div></div>
          <div className="dash-w-body">
            {activeProjects.slice(0,4).map(p=>{
              const spent=expenses.filter(e=>e.projectId===p.id).reduce((a,e)=>a+e.amount,0);
              const budgetPct=p.budget?(spent/p.budget*100).toFixed(0):null;
              const over=p.budget&&spent>p.budget;
              return(
                <div key={p.id} style={{padding:"7px 0",borderBottom:"1px solid var(--g50)"}}>
                  <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:8}}>
                    <div style={{fontSize:12,fontWeight:600,color:"var(--ink)",flex:1,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{p.name}</div>
                    {budgetPct&&<span style={{fontSize:10,fontFamily:"var(--fm)",fontWeight:600,color:over?acLight:"var(--g500)"}}>{budgetPct}%</span>}
                  </div>
                  {p.budget>0&&<div style={{display:"flex",height:4,borderRadius:2,overflow:"hidden",marginTop:4,background:"var(--g100)"}}>
                    <div style={{width:`${Math.min(budgetPct,100)}%`,background:over?acLight:acDark,borderRadius:2,transition:"width .3s"}}/>
                  </div>}
                </div>
              );
            })}
          </div>
        </div>
      ):null;

      /* ════ STAFF & HR ════ */
      case "staff": return(
        <div key={key} className="dash-w">
          <div className="dash-w-hd"><div className="dash-w-title">{isRTL?"الموظفون":"Staff"}</div><div className="dash-w-sub">{activeStaff.length} {isRTL?"نشط":"active"}</div></div>
          <div className="dash-w-body">
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8,marginBottom:8}}>
              <div className="dash-qs"><div className="dash-qs-val" style={{color:pendingLeave.length?acMid:"var(--ink)"}}>{pendingLeave.length}</div><div className="dash-qs-lbl">{isRTL?"إجازات":"Leave"}</div></div>
              <div className="dash-qs"><div className="dash-qs-val" style={{color:pendingLoans.length?acMid:"var(--ink)"}}>{pendingLoans.length}</div><div className="dash-qs-lbl">{isRTL?"سلف":"Loans"}</div></div>
              <div className="dash-qs"><div className="dash-qs-val" style={{color:staffDocExpiring.length?acDark:acLight}}>{staffDocExpiring.length}</div><div className="dash-qs-lbl">{isRTL?"وثائق":"Docs"}</div></div>
            </div>
            {staffDocExpiring.length>0&&<div style={{fontSize:10,color:acMid}}>{staffDocExpiring.slice(0,2).map(d=>`${d.staff}: ${d.doc} (${d.days}d)`).join(" · ")}</div>}
          </div>
        </div>
      );

      /* ════ FLEET & VEHICLES ════ */
      case "fleet": return (vehicles||[]).length>0?(
        <div key={key} className="dash-w">
          <div className="dash-w-hd"><div className="dash-w-title">{isRTL?"الأسطول":"Fleet"}</div><div className="dash-w-sub">{(vehicles||[]).length} {isRTL?"مركبات":"vehicles"}</div></div>
          <div className="dash-w-body">
            {fleetAlerts.length>0?(
              <div>
                <div style={{fontSize:11,fontWeight:600,color:acDark,marginBottom:8}}>{fleetAlerts.length} {isRTL?"تنبيهات":"alert"}{fleetAlerts.length>1&&!isRTL?"s":""}</div>
                {fleetAlerts.slice(0,3).map(v=>{
                  const reasons=[];
                  if(v.odom>=(v.nextServiceOdom||Infinity)) reasons.push(isRTL?"خدمة":"Service due");
                  if(new Date(v.insuranceExpiry||"2099")<now2) reasons.push(isRTL?"تأمين":"Insurance");
                  if(new Date(vehRegExpiry(v)||"2099")<now2) reasons.push(isRTL?"استمارة":"Registration");
                  return(
                    <div key={v.id} style={{display:"flex",alignItems:"center",gap:8,padding:"5px 0",borderBottom:`1px solid ${acLightBg}`}}>
                      <span style={{fontSize:11,fontWeight:600,color:"var(--ink)"}}>{v.make} {v.model}</span>
                      <span style={{fontSize:10,color:acMid,marginInlineStart:"auto"}}>{reasons.join(" · ")}</span>
                    </div>
                  );
                })}
              </div>
            ):<div style={{fontSize:12,color:acDark,fontWeight:500}}>{isRTL?"لا توجد تنبيهات":"All clear — no alerts"}</div>}
          </div>
        </div>
      ):null;

      /* ════ INCOME BREAKDOWN ════ */
      case "incomeBreak": return(
        <div key={key} className="dash-w">
          <div className="dash-w-hd"><div className="dash-w-title">{isRTL?"تفاصيل الدخل":"Income Breakdown"}</div><div className="dash-w-sub">{monthName}</div></div>
          <div className="dash-w-body">
            {monthlyRecurInc>0&&<div style={{display:"flex",alignItems:"center",gap:8,padding:"8px 12px",background:acDarkBg,borderRadius:12,marginBottom:10}}>
              <span style={{fontSize:11,color:acDark,fontWeight:600,flex:1}}>{isRTL?"دخل متكرر شهري":"Monthly Recurring"}</span>
              <span style={{fontFamily:"var(--fm)",fontSize:13,fontWeight:700,color:acDark}}>{fmtCompact(monthlyRecurInc)}</span>
            </div>}
            {incCatArr.length>0?incCatArr.slice(0,5).map(([cat,amt],ci)=>(
              <div key={cat} className="dash-mini-bar">
                <div className="dash-mini-bar-label">{cat}</div>
                <div className="dash-mini-bar-track"><div className="dash-mini-bar-fill" style={{width:`${Math.max((amt/(incCatArr[0][1]||1))*100,6)}%`,background:ci===0?acDark:ci===1?acMid:acLight}}/></div>
                <div className="dash-mini-bar-val" style={{color:acDark}}>{fmtShort(amt)}</div>
              </div>
            )):<div style={{fontSize:12,color:"var(--g400)",padding:"8px 0"}}>{isRTL?"لا يوجد دخل هذا الشهر":"No income this month"}</div>}
            {overdueIncome.length>0&&<div style={{marginTop:8,padding:"8px 12px",background:acLightBg,borderRadius:10,fontSize:11,fontWeight:600,color:acLight}}>{overdueIncome.length} {isRTL?"دخل متأخر":"overdue"} · {fmtCompact(overdueIncome.reduce((a,i)=>a+i.amount,0))}</div>}
            {recurringInc.length>0&&<div style={{marginTop:6,fontSize:10,color:"var(--g400)"}}>{recurringInc.length} {isRTL?"مصدر متكرر نشط":"active recurring source"}{recurringInc.length>1&&!isRTL?"s":""}</div>}
          </div>
        </div>
      );

      /* ════ SPENDING BY CATEGORY ════ */
      case "spendCats": return expCatArr.length>0?(
        <div key={key} className="dash-w">
          <div className="dash-w-hd"><div className="dash-w-title">{isRTL?"الإنفاق حسب الفئة":"Spending by Category"}</div><div className="dash-w-sub">{monthName}</div></div>
          <div className="dash-w-body">
            {expCatArr.slice(0,6).map(([cat,amt])=>{
              const pct=monthExp>0?Math.round(amt/monthExp*100):0;
              return(
                <div key={cat} className="dash-mini-bar">
                  <div className="dash-mini-bar-label">{cat}</div>
                  <div className="dash-mini-bar-track"><div className="dash-mini-bar-fill" style={{width:`${Math.max(pct,6)}%`,background:acLight}}/></div>
                  <div className="dash-mini-bar-val" style={{color:acLight}}>{pct}%</div>
                </div>
              );
            })}
            {recurringRenewals.length>0&&<div style={{marginTop:8,padding:"8px 12px",background:acDarkBg,borderRadius:10,fontSize:11,fontWeight:500,color:acMid}}>{recurringRenewals.length} {isRTL?"تجديد هذا الأسبوع":"renewal"}{recurringRenewals.length>1&&!isRTL?"s":""} {isRTL?"":"this week"}</div>}
          </div>
        </div>
      ):null;

      /* ════ CASH FLOW TREND ════ */
      case "cashTrend": return(
        <div key={key} className="dash-w">
          <div className="dash-w-hd"><div className="dash-w-title">{isRTL?"تدفق نقدي":"Cash Flow"}</div><div className="dash-w-sub">{isRTL?"آخر 4 أشهر":"Last 4 months"}</div></div>
          <div className="dash-w-body">
            <div style={{display:"flex",gap:6,alignItems:"flex-end",height:80,marginBottom:8}}>
              {cfMonths.map((m,i)=>(
                <div key={i} style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",gap:2}}>
                  <div style={{width:"100%",display:"flex",gap:2,alignItems:"flex-end",height:60}}>
                    <div style={{flex:1,background:acDark,borderRadius:"4px 4px 0 0",height:`${Math.max((m.inc/cfMax)*60,3)}px`,opacity:0.7,transition:"height .3s"}}/>
                    <div style={{flex:1,background:acFade,borderRadius:"4px 4px 0 0",height:`${Math.max((m.exp/cfMax)*60,3)}px`,opacity:0.7,transition:"height .3s"}}/>
                  </div>
                  <div style={{fontSize:9,fontWeight:600,color:"var(--g400)",textTransform:"uppercase"}}>{m.label}</div>
                </div>
              ))}
            </div>
            <div style={{display:"flex",justifyContent:"center",gap:16,fontSize:10,color:"var(--g400)"}}>
              <span><span style={{display:"inline-block",width:8,height:8,borderRadius:2,background:acDark,marginInlineEnd:4,verticalAlign:"middle"}}/>{isRTL?"دخل":"Income"}</span>
              <span><span style={{display:"inline-block",width:8,height:8,borderRadius:2,background:acFade,marginInlineEnd:4,verticalAlign:"middle"}}/>{isRTL?"مصروفات":"Expenses"}</span>
            </div>
            {cfMonths.length>0&&<div style={{marginTop:8,display:"grid",gridTemplateColumns:"1fr 1fr",gap:4}}>
              <div style={{fontSize:10,color:"var(--g400)"}}>{isRTL?"هذا الشهر":"This month"}</div>
              <div style={{fontSize:10,fontFamily:"var(--fm)",fontWeight:600,color:cfMonths[3].net>=0?acDark:acLight,textAlign:"right"}}>{cfMonths[3].net>=0?"+":""}{fmtShort(cfMonths[3].net)}</div>
            </div>}
          </div>
        </div>
      );

      /* ════ INVESTMENT ALLOCATION ════ */
      case "investAlloc": return holdingCatArr.length>0?(
        <div key={key} className="dash-w">
          <div className="dash-w-hd"><div className="dash-w-title">{isRTL?"توزيع الاستثمارات":"Investment Allocation"}</div></div>
          <div className="dash-w-body">
            {holdingCatArr.slice(0,5).map(([cat,val])=>{
              const pct=totalP>0?Math.round(val/totalP*100):0;
              const colors=[acDark,acMid,acLight,acFade,"var(--g500)"];
              const ci=holdingCatArr.findIndex(c=>c[0]===cat);
              return(
                <div key={cat} className="dash-mini-bar">
                  <div className="dash-mini-bar-label">{cat}</div>
                  <div className="dash-mini-bar-track"><div className="dash-mini-bar-fill" style={{width:`${Math.max(pct,6)}%`,background:colors[ci%colors.length]}}/></div>
                  <div className="dash-mini-bar-val" style={{color:colors[ci%colors.length]}}>{pct}%</div>
                </div>
              );
            })}
            {upcomingPayments.length>0&&<div style={{marginTop:10,padding:"8px 12px",background:acLightBg,borderRadius:10}}>
              <div style={{fontSize:11,fontWeight:600,color:acDark,marginBottom:2}}>{upcomingPayments.length} {isRTL?"دفعات فوائد هذا الأسبوع":"payment"}{upcomingPayments.length>1&&!isRTL?"s":""} {isRTL?"":"due this week"}</div>
              {upcomingPayments.slice(0,2).map(l=><div key={l.id} style={{fontSize:10,color:acMid}}>{l.name}: {fmtShort(l.monthlyPayment||0)} · {l.nextPaymentDate}</div>)}
            </div>}
            {liabMaturing.length>0&&<div style={{marginTop:6,fontSize:10,color:acMid,fontWeight:500}}>{liabMaturing.length} {isRTL?"التزام ينتهي خلال 90 يوم":"liabilit"}{liabMaturing.length>1&&!isRTL?"ies":"y"} {isRTL?"":"maturing within 90d"}</div>}
          </div>
        </div>
      ):null;

      /* ════ WEEK AHEAD ════ */
      case "weekAhead": return(weekMeetings.length>0||weekTransport.length>0)?(
        <div key={key} className="dash-w">
          <div className="dash-w-hd"><div className="dash-w-title">{isRTL?"الأسبوع القادم":"Week Ahead"}</div><div className="dash-w-sub">{weekMeetings.length} {isRTL?"حدث":"event"}{weekMeetings.length!==1&&!isRTL?"s":""}</div></div>
          <div className="dash-w-body">
            {weekMeetings.slice(0,5).map(m=>{
              const dayLabel=new Date(m.date).toLocaleDateString(isRTL?"ar-SA":"en-GB",{weekday:"short",day:"numeric"});
              return(
                <div key={m.id} style={{display:"flex",gap:10,padding:"6px 0",borderBottom:"1px solid var(--g50)"}}>
                  <div style={{fontFamily:"var(--fm)",fontSize:10,fontWeight:600,color:acDark,minWidth:48}}>{dayLabel}</div>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontSize:12,fontWeight:500,color:"var(--ink)",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{m.title}</div>
                    {m.time&&<div style={{fontSize:10,color:"var(--g400)"}}>{m.time?.slice(0,5)}{m.location?` · ${m.location}`:""}</div>}
                  </div>
                </div>
              );
            })}
            {weekTransport.length>0&&<div style={{marginTop:6,fontSize:11,color:acMid,fontWeight:500}}>🚗 {weekTransport.length} {isRTL?"رحلات هذا الأسبوع":"ride"}{weekTransport.length>1&&!isRTL?"s":""} {isRTL?"":"this week"}</div>}
          </div>
        </div>
      ):null;

      /* ════ PROJECT HEALTH ════ */
      case "projHealth": return activeProjects.length>0?(
        <div key={key} className="dash-w">
          <div className="dash-w-hd"><div className="dash-w-title">{isRTL?"صحة المشاريع":"Project Health"}</div><div className="dash-w-sub">{activeProjects.length} {isRTL?"نشط":"active"}</div></div>
          <div className="dash-w-body">
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8,marginBottom:10}}>
              <div className="dash-qs"><div className="dash-qs-val" style={{color:overBudgetProjects.length?acLight:acDark}}>{overBudgetProjects.length}</div><div className="dash-qs-lbl">{isRTL?"تجاوز":"Over Budget"}</div></div>
              <div className="dash-qs"><div className="dash-qs-val" style={{color:behindProjects.length?acMid:acDark}}>{behindProjects.length}</div><div className="dash-qs-lbl">{isRTL?"متأخر":"Behind"}</div></div>
              <div className="dash-qs"><div className="dash-qs-val">{projMilestones.length}</div><div className="dash-qs-lbl">{isRTL?"معالم":"Milestones"}</div></div>
            </div>
            {overBudgetProjects.slice(0,2).map(p=>{
              const spent=expenses.filter(e=>e.projectId===p.id).reduce((a,e)=>a+e.amount,0);
              return(
                <div key={p.id} style={{display:"flex",alignItems:"center",gap:8,padding:"5px 0",fontSize:11}}>
                  <span style={{width:6,height:6,borderRadius:3,background:acLight,flexShrink:0}}/>
                  <span style={{flex:1,color:"var(--ink)",fontWeight:500,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{p.name}</span>
                  <span style={{fontFamily:"var(--fm)",color:acLight,fontWeight:600,fontSize:10}}>{fmtShort(spent)}/{fmtShort(p.budget)}</span>
                </div>
              );
            })}
          </div>
        </div>
      ):null;

      /* ════ STAFF INSIGHTS ════ */
      case "staffInsight": return activeStaff.length>0?(
        <div key={key} className="dash-w">
          <div className="dash-w-hd"><div className="dash-w-title">{isRTL?"رؤى الموظفين":"Staff Insights"}</div></div>
          <div className="dash-w-body">
            {totalPayroll>0&&<div style={{display:"flex",alignItems:"center",gap:8,padding:"8px 12px",background:"var(--g50)",borderRadius:12,marginBottom:10}}>
              <span style={{fontSize:11,color:"var(--g500)",fontWeight:500,flex:1}}>{isRTL?"رواتب شهرية":"Monthly Payroll"}</span>
              <span style={{fontFamily:"var(--fm)",fontSize:14,fontWeight:700,color:"var(--ink)"}}>{fmtCompact(totalPayroll)}</span>
            </div>}
            <div style={{display:"flex",flexWrap:"wrap",gap:4,marginBottom:8}}>
              {staffRoleArr.slice(0,5).map(([role,count])=>(
                <span key={role} className="dash-chip">{role} <strong>{count}</strong></span>
              ))}
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:4}}>
              {activeLoans>0&&<DM l={isRTL?"قروض نشطة":"Active Loans"} v={activeLoans} c={acMid}/>}
              {pendingLeave.length>0&&<DM l={isRTL?"طلبات إجازة":"Leave Requests"} v={pendingLeave.length} c={acDark}/>}
            </div>
          </div>
        </div>
      ):null;

      /* ════ HOUSEHOLD ════ */
      case "household": return(
        <div key={key} className="dash-w">
          <div className="dash-w-hd"><div className="dash-w-title">{isRTL?"المنزل":"Household"}</div></div>
          <div className="dash-w-body">
            <div className="dash-qs">
              <div className="dash-qs-val">{pendingGroceries.length}</div>
              <div className="dash-qs-lbl">{isRTL?"بقالة معلقة":"Pending Grocery"}</div>
              {totalGroceries>0&&<div style={{marginTop:4,height:4,borderRadius:2,background:"var(--g100)",overflow:"hidden"}}><div style={{width:`${groceryPct}%`,height:"100%",background:acDark,borderRadius:2}}/></div>}
            </div>
          </div>
        </div>
      );

      /* ════ FACILITY MAINTENANCE ════ */
      case "facilMaint": return(
        <div key={key} className="dash-w">
          <div className="dash-w-hd"><div className="dash-w-title">{isRTL?"صيانة المرافق":"Facility"}</div></div>
          <div className="dash-w-body">
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8,marginBottom:8}}>
              <div className="dash-qs" style={{background:overdueMaint.length?acDarkBg:"var(--g50)"}}><div className="dash-qs-val" style={{color:overdueMaint.length?acDark:"var(--ink)"}}>{overdueMaint.length}</div><div className="dash-qs-lbl">{isRTL?"متأخر":"Overdue"}</div></div>
              <div className="dash-qs"><div className="dash-qs-val">{scheduledMaint.length}</div><div className="dash-qs-lbl">{isRTL?"مجدول":"Scheduled"}</div></div>
              <div className="dash-qs"><div className="dash-qs-val">{unassignedMaint.length}</div><div className="dash-qs-lbl">{isRTL?"غير مسند":"Unassigned"}</div></div>
            </div>
            {maintCosts>0&&<DM l={isRTL?"تكاليف هذا الشهر":"Cost this month"} v={fmtCompact(maintCosts)} c={acMid}/>}
          </div>
        </div>
      );

      /* ════ TRANSPORT ════ */
      case "transport": return upcomingTransport.length>0?(
        <div key={key} className="dash-w">
          <div className="dash-w-hd"><div className="dash-w-title">{isRTL?"النقل":"Transport"}</div><div className="dash-w-sub">{upcomingTransport.length} {isRTL?"قادم":"upcoming"}</div></div>
          <div className="dash-w-body">
            {upcomingTransport.slice(0,4).map(r=>{
              const dayLabel=r.date===TODAY?(isRTL?"اليوم":"Today"):new Date(r.date).toLocaleDateString(isRTL?"ar-SA":"en-GB",{weekday:"short",day:"numeric"});
              return(
                <div key={r.id} style={{display:"flex",gap:10,padding:"6px 0",borderBottom:"1px solid var(--g50)"}}>
                  <div style={{fontFamily:"var(--fm)",fontSize:10,fontWeight:600,color:acDark,minWidth:48}}>{dayLabel}</div>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontSize:12,fontWeight:500,color:"var(--ink)",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{r.pickupLoc||"Pickup"} → {r.dropoffLoc||"Dropoff"}</div>
                    {r.time&&<div style={{fontSize:10,color:"var(--g400)"}}>{r.time?.slice(0,5)}</div>}
                  </div>
                  <span style={{fontSize:9,padding:"2px 6px",borderRadius:8,background:r.status==="scheduled"?acDarkBg:acLightBg,color:r.status==="scheduled"?acDark:acMid,fontWeight:600,alignSelf:"center"}}>{r.status}</span>
                </div>
              );
            })}
          </div>
        </div>
      ):null;

      /* ════ MENU PLANNING ════ */
      case "menuPlan": return(activeCycle||totalMeals>0)?(
        <div key={key} className="dash-w">
          <div className="dash-w-hd"><div className="dash-w-title">{isRTL?"قائمة الطعام":"Menu"}</div>{activeCycle&&<div className="dash-w-sub">{activeCycle.name||""}</div>}</div>
          <div className="dash-w-body">
            {todayMeals.length>0?(
              <div>
                <div style={{fontSize:10,fontWeight:600,color:"var(--g400)",textTransform:"uppercase",letterSpacing:".06em",marginBottom:6}}>{isRTL?"وجبات اليوم":"Today's Meals"}</div>
                {todayMeals.slice(0,4).map((m,i)=>(
                  <div key={i} style={{display:"flex",alignItems:"center",gap:8,padding:"5px 0",borderBottom:"1px solid var(--g50)"}}>
                    <span style={{width:24,height:24,borderRadius:8,background:acDarkBg,color:acDark,display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,flexShrink:0}}>{m.mealType==="breakfast"?"🌅":m.mealType==="lunch"?"☀️":"🌙"}</span>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontSize:12,fontWeight:500,color:"var(--ink)",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{m.dishName||m.name||m.mealType}</div>
                      <div style={{fontSize:10,color:"var(--g400)",textTransform:"capitalize"}}>{m.mealType}</div>
                    </div>
                  </div>
                ))}
              </div>
            ):<div style={{fontSize:12,color:"var(--g400)"}}>{activeCycle?(isRTL?"لا وجبات مخطط لها اليوم":"No meals planned today"):(isRTL?"لا يوجد جدول نشط":"No active menu cycle")}</div>}
            {totalMeals>0&&<div style={{marginTop:6,fontSize:10,color:"var(--g400)"}}>{totalMeals} {isRTL?"وجبة في المكتبة":"meal"}{totalMeals>1&&!isRTL?"s":""} {isRTL?"":"in library"}</div>}
          </div>
        </div>
      ):null;

      /* ════ SUPPLIERS ════ */
      case "suppliers": return activeSuppliers.length>0?(
        <div key={key} className="dash-w">
          <div className="dash-w-hd"><div className="dash-w-title">{isRTL?"الموردون":"Suppliers"}</div><div className="dash-w-sub">{activeSuppliers.length} {isRTL?"نشط":"active"}</div></div>
          <div className="dash-w-body">
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:8}}>
              <div className="dash-qs"><div className="dash-qs-val">{activeSuppliers.length}</div><div className="dash-qs-lbl">{isRTL?"نشط":"Active"}</div></div>
              <div className="dash-qs" style={{background:expiringContracts.length?acDarkBg:"var(--g50)"}}><div className="dash-qs-val" style={{color:expiringContracts.length?acMid:"var(--ink)"}}>{expiringContracts.length}</div><div className="dash-qs-lbl">{isRTL?"عقود تنتهي":"Expiring"}</div></div>
            </div>
            {avgRating!=="—"&&<DM l={isRTL?"متوسط التقييم":"Avg Rating"} v={`★ ${avgRating}`} c={acMid}/>}
          </div>
        </div>
      ):null;

      /* ════ SMART HOME ════ */
      case "smartHome": {
        const shConns=state.smartHomeConnections||{};
        const shConnected=Object.values(shConns).filter(c=>c.status==="connected");
        if(!shConnected.length) return null;
        return(
          <div key={key} className="dash-w">
            <div className="dash-w-hd"><div className="dash-w-title">{isRTL?"المنزل الذكي":"Smart Home"}</div><div className="dash-w-sub">{shConnected.length} {isRTL?"موقع":"location"}{shConnected.length!==1&&!isRTL?"s":""}</div></div>
            <div className="dash-w-body">
              {shConnected.slice(0,3).map(c=>(
                <div key={c.locId} style={{display:"flex",alignItems:"center",gap:8,padding:"6px 0",borderBottom:"1px solid var(--g50)"}}>
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke={acDark} strokeWidth="1.3"><path d="M1.4 6.3L7 1.4l5.6 4.9"/><path d="M2.8 5.9V12.6H11.2V5.9"/></svg>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontSize:12,fontWeight:500,color:"var(--ink)",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{c.label||c.locId}</div>
                  </div>
                  <span style={{fontSize:9,padding:"2px 6px",borderRadius:8,background:acDarkBg,color:acDark,fontWeight:600}}>{isRTL?"متصل":"Connected"}</span>
                </div>
              ))}
            </div>
          </div>
        );
      }

      /* ════ FLEET OVERVIEW ════ */
      case "fleetValue": return(vehicles||[]).length>0?(
        <div key={key} className="dash-w">
          <div className="dash-w-hd"><div className="dash-w-title">{isRTL?"نظرة على الأسطول":"Fleet Overview"}</div></div>
          <div className="dash-w-body">
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:8}}>
              <div className="dash-qs"><div className="dash-qs-val">{fmtShort(totalFleetValue)}</div><div className="dash-qs-lbl">{isRTL?"القيمة الحالية":"Current Value"}</div></div>
              <div className="dash-qs"><div className="dash-qs-val" style={{color:fleetDepreciation>0?acLight:acDark}}>{fmtShort(fleetDepreciation)}</div><div className="dash-qs-lbl">{isRTL?"الاستهلاك":"Depreciation"}</div></div>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:4}}>
              <DM l={isRTL?"إجمالي المركبات":"Vehicles"} v={(vehicles||[]).length} bold/>
              <DM l={isRTL?"متوسط الأميال":"Avg Mileage"} v={avgMileage.toLocaleString()+" km"}/>
            </div>
          </div>
        </div>
      ):null;

      default: return null;
    }
  };

  return(
    <div className="page dash-page">
      {/* Greeting */}
      <div className="dash-greeting">
        <div className="dash-greeting-text">{greeting}, {isRTL&&currentUser.nameAr?currentUser.nameAr:(currentUser.name||"").split(" ")[0]}</div>
        {locLabel&&<div style={{fontSize:12,color:"var(--g400)",marginTop:2}}>{locLabel}</div>}
      </div>

      {/* Widget grid */}
      <div className="dash-grid">
        {orderedWidgets.map(key=>W(key))}
      </div>

      {/* AI */}
      <div className="dash-ai-wrap" style={{marginTop:12}}><AIPanel title={isRTL?"الذكاء":"Intelligence"} context={aiCtx} prompt="Top 5 to act on today. What is stuck or at risk?" lang={lang} moduleKey="Dashboard" wsState={state}/></div>

      {/* Customize modal */}
      {showCustomize&&<Modal><div className="mo" onClick={()=>setShowCustomize(false)}>
        <div className="md" onClick={e=>e.stopPropagation()} style={{maxWidth:400}}>
          <div className="mh"><div style={{flex:1}}><div className="mt">{isRTL?"تخصيص لوحة التحكم":"Customize Dashboard"}</div></div><button onClick={()=>setShowCustomize(false)} style={{width:28,height:28,borderRadius:14,background:"var(--g200)",border:"none",display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",flexShrink:0,transition:"background .15s"}}><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="var(--g500)" strokeWidth="2.5" strokeLinecap="round"><line x1="6" y1="6" x2="18" y2="18"/><line x1="18" y1="6" x2="6" y2="18"/></svg></button></div>
          <div className="mb">
            <div style={{fontSize:12,color:"var(--g500)",marginBottom:14}}>{isRTL?"فعّل أو أوقف العناصر وأعد ترتيبها":"Toggle widgets on or off and reorder them."}</div>
            {draftOrder.map((key,i)=>{
              const def=DASHBOARD_WIDGET_DEFS.find(d=>d.key===key);if(!def) return null;
              const hidden=draftHidden.includes(key);
              const isTouchDrag=touchDragIdx===i;
              const isDeskDrag=dragIdx===i;
              const isLifted=isTouchDrag||isDeskDrag;
              return(
                <div key={key} data-drag-item draggable onDragStart={(e)=>handleDragStart(e,i)} onDragOver={(e)=>handleDragOver(e,i)} onDrop={handleDrop} onDragEnd={handleDragEnd} onTouchStart={(e)=>handleTouchStart(e,i)} onTouchMove={handleTouchMove} onTouchEnd={handleTouchEnd} style={{display:"flex",alignItems:"center",gap:10,padding:"10px 12px",borderBottom:"1px solid var(--g100)",background:isLifted?"var(--white)":"transparent",borderRadius:isLifted?14:0,boxShadow:isLifted?"0 8px 30px rgba(0,0,0,.12), 0 2px 8px rgba(0,0,0,.08)":"none",transform:isTouchDrag?`translateY(${touchDragY}px) scale(1.03)`:isLifted?"scale(1.02)":"scale(1)",zIndex:isLifted?10:0,position:"relative",transition:isLifted?"box-shadow .15s":"transform .2s, background .15s, box-shadow .2s, border-radius .15s",cursor:"grab",WebkitUserSelect:"none",userSelect:"none",touchAction:"none"}}>
                  <div style={{display:"flex",flexDirection:"column",gap:1,padding:"0 2px",color:isLifted?acDark:"var(--g300)",flexShrink:0,transition:"color .15s"}}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="8" y1="7" x2="16" y2="7"/><line x1="8" y1="12" x2="16" y2="12"/><line x1="8" y1="17" x2="16" y2="17"/></svg>
                  </div>
                  <div style={{flex:1,minWidth:0}}><div style={{fontSize:13,fontWeight:isLifted?600:500,color:hidden?"var(--g400)":"var(--ink)",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis",transition:"font-weight .15s"}}>{isRTL&&def.labelAr?def.labelAr:def.label}</div><div style={{fontSize:10,color:"var(--g400)"}}>{isRTL&&def.groupAr?def.groupAr:def.group}</div></div>
                  <button onClick={(e)=>{e.stopPropagation();setDraftHidden(h=>h.includes(key)?h.filter(k=>k!==key):[...h,key]);}} style={{width:40,height:22,borderRadius:11,border:"none",background:hidden?"var(--g200)":acDark,cursor:"pointer",position:"relative",transition:"background .2s",flexShrink:0}}>
                    <div style={{width:16,height:16,borderRadius:8,background:"white",position:"absolute",top:3,left:hidden?3:21,transition:"left .2s",boxShadow:"0 1px 3px rgba(0,0,0,.15)"}}/>
                  </button>
                </div>
              );
            })}
          </div>
          <div className="mf" style={{display:"flex",gap:8}}>
            <button className="btn btn-p" style={{flex:1,textAlign:"center",justifyContent:"center"}} onClick={saveCustomize}>{isRTL?"حفظ":"Save"}</button>
            <button className="btn btn-s" style={{flex:1,textAlign:"center",justifyContent:"center"}} onClick={()=>{setDraftOrder(DEFAULT_DASHBOARD_ORDER);setDraftHidden([]);}}>{isRTL?"إعادة تعيين":"Reset"}</button>
          </div>
        </div>
      </div></Modal>}

      {/* Customize FAB */}
      <button className="dash-customize-btn" onClick={openCustomize}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="4" y1="21" x2="4" y2="14"/><line x1="4" y1="10" x2="4" y2="3"/><line x1="12" y1="21" x2="12" y2="12"/><line x1="12" y1="8" x2="12" y2="3"/><line x1="20" y1="21" x2="20" y2="16"/><line x1="20" y1="12" x2="20" y2="3"/><line x1="1" y1="14" x2="7" y2="14"/><line x1="9" y1="8" x2="15" y2="8"/><line x1="17" y1="16" x2="23" y2="16"/></svg>
      </button>
    </div>
  );
}

export { DASHBOARD_WIDGET_DEFS, DEFAULT_DASHBOARD_ORDER };
export default CommandCenter;
